/**
 * MediaPanelContentRenderer - Selects and renders the appropriate layout renderer
 *
 * This component handles the selection of which renderer to use (gallery, compare, grid)
 * based on the mode configuration, and wraps all renderers in a consistent scrollable container.
 */

import {type MediaCardType} from '@wandb/weave/common/types/media';
import React, {useMemo} from 'react';
import useResizeObserver from 'use-resize-observer';

import {type CustomRunNames} from '../../state/views/customRunNames/types';
import {type PanelObjSchema} from '../../state/views/panel/types';
import {type PartRefFromObjSchema} from '../../state/views/types';
import {useRampFlagMediaCompareModeDiscovery} from '../../util/rampFeatureFlags/rampFeatureFlags';
import {type LayoutMode} from './common';
import {
  type MediaBrowserPanelProps,
  type MediaConfigWithDefaults,
} from './Component';
import {GridRenderer, type GridRendererProps} from './layout/renderers';
import {ContentFooterActionSlot} from './layout/slots/ContentFooterActionSlot';
import {ContentFooterSlot} from './layout/slots/ContentFooterSlot';
import {ContentHeaderSlot} from './layout/slots/ContentHeaderSlot';
import {type GridData, type SlotRegistry} from './layout/types';
import {type TilePagination} from './tiles';
import {type StepMeta} from './types';

export type MediaPanelContentRendererProps = {
  mode: LayoutMode;
  gridData: GridData;
  slots: SlotRegistry;
  config: MediaConfigWithDefaults;
  updateConfig: MediaBrowserPanelProps['updateConfig'];
  stepMeta: StepMeta;
  isMaxStepLoading: boolean;
  mediaBrowserSize: {width: number; height: number};
  cardWidth: number;
  cardHeight: number;
  mediaType: MediaCardType;
  mediaSliderKey: string;
  panelRef: PartRefFromObjSchema<PanelObjSchema>;
  disableRunLinks?: boolean;
  customRunNames?: CustomRunNames;
  shrunk?: boolean;
  allMasks?: any;
  tilePagination: TilePagination | null;
  mediaBrowserRefCallback: (el: HTMLDivElement) => void;
  panelViewerRef: React.MutableRefObject<HTMLDivElement | null>;
  isFullScreenPanelPage?: boolean;
};

/**
 * MediaPanelContentRenderer - Renders content with header, grid, and footer.
 *
 * This component handles:
 * - Content header slot (with optional pagination)
 * - Grid renderer
 * - Content footer slot
 *
 * Sizing and overflow behavior are managed by the parent MediaPanelContent component.
 *
 * @param props - MediaPanelContentRendererProps
 * @returns React element with full content layout
 */
export const MediaPanelContentRenderer: React.FC<
  MediaPanelContentRendererProps
> = ({
  mode,
  gridData,
  slots,
  config,
  updateConfig,
  stepMeta,
  isMaxStepLoading,
  mediaBrowserSize,
  cardWidth,
  cardHeight,
  mediaType,
  mediaSliderKey,
  panelRef,
  disableRunLinks,
  customRunNames,
  shrunk,
  allMasks,
  tilePagination,
  mediaBrowserRefCallback,
  panelViewerRef,
  isFullScreenPanelPage = false,
}) => {
  const {ref: contentFooterRef, width: contentFooterWidth} =
    useResizeObserver<HTMLDivElement>();
  const shouldShowCompareModeDiscovery = useRampFlagMediaCompareModeDiscovery();

  // Extract slot locations
  /**
   * At the point where we configure the possible controls for the slost,
   * we don't know whether we have the volume of data to paginate.
   * Now that we know, we want to avoid rendering an empty control slot.
   */
  const contentHeaderControls = useMemo(() => {
    const allHeaderControls = slots.contentHeader || [];
    if (tilePagination != null) {
      return allHeaderControls;
    }
    return allHeaderControls.filter(c => c.control !== 'pagination');
  }, [slots.contentHeader, tilePagination]);
  const contentFooterControls = slots.contentFooter || [];
  const contentFooterActionControls = slots.contentFooterAction || [];
  const shouldRenderContentFooterAction =
    contentFooterActionControls.length > 0 && shouldShowCompareModeDiscovery;
  // In compare fullscreen, let the children (column headers + grid + footers) define the
  // minimum width via min-content so the panel-viewer only scrolls when they truly can't shrink.
  // This avoids calculation/rounding errors from a pixel-based minWidth.
  const shouldUseContentDrivenMinWidth =
    config.mode === 'compare' && isFullScreenPanelPage;

  const baseProps: GridRendererProps = {
    gridData,
    slots,
    config,
    updateConfig,
    stepMeta,
    isMaxStepLoading,
    mediaBrowserSize,
    cardWidth,
    cardHeight,
    mediaType,
    mediaSliderKey,
    panelRef,
    disableRunLinks,
    customRunNames,
    shrunk,
    allMasks,
    isFullScreenPanelPage,
  };

  const includeBorders = mode === 'compare';
  const contentBorderClasses = includeBorders
    ? 'border-t border-wb-border rounded-t-md'
    : '';
  const contentHeaderBorderClasses = includeBorders
    ? 'border-x border-b border-wb-border'
    : '';
  const contentFooterBorderClasses =
    includeBorders && contentFooterControls.length > 0
      ? 'border-x border-wb-border'
      : '';
  const shouldRenderContentFooter =
    contentFooterControls.length > 0 || shouldRenderContentFooterAction;

  // Horizontal scroll wrapper so header, grid (column headers + content + column footers), and content footer scroll together.
  return (
    <div className="panel-media h-full" ref={mediaBrowserRefCallback}>
      <div
        ref={panelViewerRef}
        className={`panel-viewer h-full flex flex-col overflow-y-hidden min-w-0 w-full ${contentBorderClasses}`}>
        <div
          className={`flex flex-col flex-1 min-h-0 ${shouldUseContentDrivenMinWidth ? 'min-w-min' : 'min-w-0'}`}>
          {/* Content Header - Full width controls above content */}
          {contentHeaderControls.length > 0 && (
            <div
              className={`content-header shrink-0 flex items-center justify-between w-full ${contentHeaderBorderClasses}`}>
              <ContentHeaderSlot
                controls={contentHeaderControls}
                config={config}
                updateConfig={updateConfig}
                stepMeta={stepMeta}
                mediaSliderKey={mediaSliderKey}
                disableRunLinks={disableRunLinks}
                isFullScreenPanelPage={isFullScreenPanelPage}
                tilePagination={tilePagination}
                panelViewerRef={panelViewerRef}
              />
            </div>
          )}

          {/* Grid Renderer - flex-1 to take remaining space */}
          <div className="flex-1 min-h-0">
            <GridRenderer {...baseProps} tilePagination={tilePagination} />
          </div>

          {/* Content Footer - Controls with optional trailing actions below content */}
          {shouldRenderContentFooter && (
            <div
              className={`shrink-0 min-w-0 w-full flex items-center gap-8 px-8 pb-8 [container-type:inline-size] ${contentFooterBorderClasses}`}>
              <div className="min-w-0 flex-1" ref={contentFooterRef}>
                {contentFooterControls.length > 0 && (
                  <ContentFooterSlot
                    controls={contentFooterControls}
                    config={config}
                    updateConfig={updateConfig}
                    stepMeta={stepMeta}
                    mediaSliderKey={mediaSliderKey}
                    mediaType={mediaType}
                    isMaxStepLoading={isMaxStepLoading}
                    mediaBrowserSize={mediaBrowserSize}
                    gridData={gridData}
                    contentFooterWidth={contentFooterWidth}
                    isFullScreenPanelPage={isFullScreenPanelPage}
                  />
                )}
              </div>
              {shouldRenderContentFooterAction && (
                <ContentFooterActionSlot
                  controls={contentFooterActionControls}
                  config={config}
                  updateConfig={updateConfig}
                  stepMeta={stepMeta}
                  isMaxStepLoading={isMaxStepLoading}
                  mediaBrowserSize={mediaBrowserSize}
                  gridData={gridData}
                  isFullScreenPanelPage={isFullScreenPanelPage}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
